import React, { useEffect, useState } from "react";
import {
  RiskMap,
  RiskMapSkeletonLoader,
} from "../../components/charts/DashboardCharts";
import styles from "./dashboard.module.scss";
import { connect } from "react-redux";
import {
  getPJMDashboardSummaryRequest,
  getPJMDashboardDetailRequest,
  clearPJMDashboardData,
} from "../../store/actions/general.actions";
import {
  pjmDashboardSummary,
  pjmDashboardDetail,
} from "../../store/selectors/general.selector";
import HealthTable from "./components/HealthTable";
import { ArrowRight, Dot, Fullscreen, Minimize2 } from "lucide-react";
import Risk from "../risk";
import { useDispatch, useSelector } from "react-redux";
import Timeline from "../timeline/timeline";
import { SharedTimelineContextProvider } from "../timeline/SharedTimelineContext";
import { FunnelX } from "../../modules/default-svg";
import OverviewTable from "./OverviewTable";
import SingleOverviewTable from "./SingleOverviewTable";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import ResizeHandle from "../../components/ResizeHandle/ResizeHandle";
import { Button } from "@fluentui/react-components";

const ProjectManagerDashboard = (props) => {
  const {
    ProjectId,
    setProjectId,
    setProjectName,
    getPJMDashboardSummaryRequest,
    getPJMDashboardDetailRequest,
    clearPJMDashboardData,
    summary,
  } = props;
  const [projectHealth, setProjectHealth] = useState(null);
  const [risks, setRisks] = useState([]);
  const [isFullHeight, setIsFullHeight] = useState(false);
  const [severityNumber, setSeverityNumber] = useState(null);
  const [highlightedRiskMapCell, setHighlightedRiskMapCell] = useState(null);
  const [overviewDetails, setOverviewDetails] = useState(null);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [isOverviewMounted, setIsOverviewMounted] = useState(false);
  const [loadingHealth, setLoadingHealth] = useState(true);
  const [isHealthMounted, setIsHealthMounted] = useState(false);
  const [isTimelineFullscreen, setIsTimelineFullscreen] = useState(false);
  const dispatch = useDispatch();

  const toggleTimelineFullScreen = () => {
    setIsTimelineFullscreen(!isTimelineFullscreen);
  }
  useEffect(() => {
    dispatch(clearPJMDashboardData());
    setHighlightedRiskMapCell(null);
    setOverviewDetails(null);
    setProjectHealth(null);
    setLoadingOverview(true);
    setLoadingHealth(true);
    if (ProjectId) {
      setSeverityNumber(null);
      getPJMDashboardDetailRequest(ProjectId);
    } else {
      setIsTimelineFullscreen(false);
      getPJMDashboardSummaryRequest();
    }
  }, [ProjectId]);

  useEffect(() => {
    summary.hasOwnProperty("risks") && setRisks(summary.risks);
    summary.hasOwnProperty("health") && setProjectHealth(summary.health);
    summary.hasOwnProperty("projects") && setOverviewDetails(summary.projects);
  }, [summary]);

  useEffect(() => {
    if (overviewDetails && isOverviewMounted) {
      setLoadingOverview(false);
    }
  }, [overviewDetails, isOverviewMounted]);

  useEffect(() => {
    if (projectHealth && isHealthMounted) {
      setLoadingHealth(false);
    }
  }, [projectHealth, isHealthMounted]);

  const handleCellClick = (severity, cellData) => {
    setSeverityNumber({impact: cellData.x, probability: cellData.y});
    setHighlightedRiskMapCell({ x: cellData.x, y: cellData.y });
  };
  return (
    <>
      <div 
          className={`mt-4 ${styles.SummaryPanel}`} 
          style={ isTimelineFullscreen ? {maxHeight: "calc(100vh - 190px)" , overflow: 'auto'} :{}}
      >
        {!isTimelineFullscreen ?
          (
            <>
                      <div className="row g-3 mb-3">
              <div className="col-12">
                {ProjectId ? (
                  <SingleOverviewTable
                    overviewDetails={overviewDetails}
                    dashboardFilterSetId={setProjectId}
                    dashboardFilterSetName={setProjectName}
                    loading={loadingOverview}
                    onComponentReady={() => setIsOverviewMounted(true)}
                    entityType="Project"
                  />
                ) : (
                  <OverviewTable
                    overviewDetails={overviewDetails}
                    dashboardFilterSetId={setProjectId}
                    dashboardFilterSetName={setProjectName}
                    loading={loadingOverview}
                    onComponentReady={() => setIsOverviewMounted(true)}
                    entityType="Project"
                  />
                )}
              </div>
          </div>
        <PanelGroup direction="vertical">
          <Panel defaultSize={100}>
            <div className="row g-3 mb-3">
              <div className="col-12">
                <div className="DashboardCard RiskCard dbTableCard">
                  <div className="d-flex align-items-center justify-content-between mb-6 DashboardCardHeader">
                    <h5 className="font-weight-500">Risks</h5>
                  </div>
                  <div className="d-flex flex-column h-100 align-items-center">
                    <Risk
                      overviewType="Dashboard"
                      dashboardFilterSetProjectId={setProjectId}
                      dashboardFilterSetProjectName={setProjectName}
                      {...(ProjectId ? { projectID: ProjectId } : {})}
                      {...(severityNumber
                        ? { severityFilter: severityNumber }
                        : {})}
                      key={`risk-${ProjectId || "noProject"}-${severityNumber || "noSeverity"
                        }`}
                      loadingSkeleton={loadingOverview}
                    />
                  </div>
                </div>
              </div>
            </div>    
          </Panel>
          <ResizeHandle/>
          <Panel defaultSize={100}>
            <div className="row g-3 mb-3 h-[389px]">
              <PanelGroup direction="horizontal">
                <Panel defaultSize={70}>
                  <div className="h-full">
                    <div className="DashboardCard dbTableCard">
                      <div className="d-flex align-items-center justify-content-between mb-6 DashboardCardHeader">
                        <h5 className="font-weight-500">Health Status</h5>
                        <div className="d-flex justify-content-center align-items-center gap-3">
                          <div className="d-flex justify-content-center align-items-center gap-3">
                            <div className="d-flex align-items-center justify-content-center gap-1 text-xsm-400">
                              <div className="current">
                                <Dot
                                  size={20}
                                  strokeWidth={8}
                                  absoluteStrokeWidth
                                  color="#AAAAAA"
                                />
                              </div>
                              Current
                            </div>
                            <div className="d-flex align-items-center justify-content-center gap-1 text-xsm-400">
                              <div className="trending">
                                <ArrowRight
                                  size={15}
                                  strokeWidth={2}
                                  absoluteStrokeWidth
                                  color="#AAAAAA"
                                />
                              </div>
                              Trending
                            </div>
                            <div className="d-flex align-items-center justify-content-center gap-1 text-xsm-400">
                              <div className="HealthBadge OnTrack"></div>
                              On Target
                            </div>
                            <div className="d-flex align-items-center justify-content-center gap-1 text-xsm-400">
                              <div className="HealthBadge OffTrack"></div>
                              Monitoring
                            </div>
                            <div className="d-flex align-items-center justify-content-center gap-1 text-xsm-400">
                              <div className="HealthBadge AtRisk"></div>
                              Slipping
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="d-flex flex-column">
                        <HealthTable
                          projectHealth={projectHealth}
                          dashboardFilterSetId={setProjectId}
                          dashboardFilterSetName={setProjectName}
                          entityType="project"
                          loading={loadingHealth}
                          onComponentReady={() => setIsHealthMounted(true)}
                        />
                      </div>
                    </div>
                  </div>
                </Panel>
                <ResizeHandle orientation="vertical"/>
                <Panel defaultSize={30}>
                  <div className="h-full">
                    <div className="DashboardCard dbSmallCard">
                      <div className="d-flex align-items-center justify-content-between mb-6 DashboardCardHeader">
                        <div className="d-flex align-items-center justify-content-start">
                          <h5 className="font-weight-500">Risk Map</h5>
                          {severityNumber && (
                              <span
                                className="cursor-pointer ms-6"
                                onClick={() => {
                                  setSeverityNumber(null);
                                  setHighlightedRiskMapCell(null);
                                }}
                              >
                                <FunnelX />
                              </span>
                            )}
                        </div>

                        <div className="d-flex justify-content-end align-items-center gap-3">
                          <div className="d-flex justify-content-center align-items-center gap-3">
                            <div className="d-flex align-items-center justify-content-center gap-1 text-xsm-400">
                              <div className="RiskBadge Low"></div>
                              Low
                            </div>
                            <div className="d-flex align-items-center justify-content-center gap-1 text-xsm-400">
                              <div className="RiskBadge Moderate"></div>
                              Moderate
                            </div>
                            <div className="d-flex align-items-center justify-content-center gap-1 text-xsm-400">
                              <div className="RiskBadge High"></div>
                              High
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="d-flex flex-column h-100 align-items-center justify-content-center DashboardCardBody">
                        {loadingOverview ? (
                          <RiskMapSkeletonLoader />
                        ) : (
                          <RiskMap
                            riskData={risks}
                            onCellClick={handleCellClick}
                            highlightedRiskMapCell={highlightedRiskMapCell}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </Panel>
              </PanelGroup>
            </div>         
          </Panel>
          {ProjectId && (
          <>
            <ResizeHandle/>
            <Panel defaultSize={100}>
            <div className="row g-3 mt-3">
              <div className="col-12">
                <div className="DashboardCard dbTableCard">
                  <div className="d-flex align-items-center justify-content-between mb-6 DashboardCardHeader">
                    <h5 className="font-weight-500">Timeline</h5>
                    <Button
                      appearance="subtle"
                      size="medium"
                      icon={<Fullscreen />}
                      onClick={toggleTimelineFullScreen}
                    />
                  </div>
                  <div className="d-flex flex-column h-100 align-items-center justify-content-center">
                    <SharedTimelineContextProvider>
                      <Timeline
                        entityID={ProjectId}
                        entityType="project"
                        isFullHeight={isFullHeight}
                        setFullHeight={setIsFullHeight}
                      />
                    </SharedTimelineContextProvider>
                  </div>
                </div>
              </div>
            </div>
          </Panel>
          </>
        )}
        </PanelGroup>
            </>
          )
          :
            <div className="row">
              <div className="col-12">
                <div className="DashboardCard dbTableCard">
                  <div className="d-flex align-items-center justify-content-between mb-6 DashboardCardHeader">
                    <h5 className="font-weight-500">Timeline</h5>
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<Minimize2 />}
                      onClick={toggleTimelineFullScreen}
                    />
                  </div>
                  <div className="d-flex flex-column h-100 align-items-center justify-content-center">
                    <SharedTimelineContextProvider>
                      <Timeline
                        entityID={ProjectId}
                        entityType="project"
                        isFullHeight={isTimelineFullscreen}
                        setFullHeight={setIsFullHeight}
                      />
                    </SharedTimelineContextProvider>
                  </div>
                </div>
              </div>
            </div>
        }

      </div>
    </>
  );
};

const mapDispatchToProps = {
  getPJMDashboardSummaryRequest,
  getPJMDashboardDetailRequest,
  clearPJMDashboardData,
};

const mapStateToProps = (state, ownProps) => {
  const summary = pjmDashboardSummary(state);
  const detail = pjmDashboardDetail(state);
  if (ownProps.ProjectId) {
    return {
      summary: detail && Object.keys(detail).length > 0 ? detail : summary,
    };
  } else {
    return { summary: summary };
  }
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ProjectManagerDashboard);
